import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { useLanguage } from '../../context/LanguageContext';

const { 
  FiGrid, FiSliders, FiZap, FiTrendingUp, FiCheckCircle, FiArrowRight,
  FiLayers, FiRefreshCw, FiCheck, FiCpu, FiMessageSquare, FiGlobe
} = FiIcons;

const NimaTab = () => {
  const { t } = useLanguage();
  const [activeWorkflow, setActiveWorkflow] = useState('Ticket Sales');

  const features = [
    {
      icon: FiGrid,
      title: t('solutionsPage.nima.features.f1Title') || "Scale with 1K+ Apps",
      description: t('solutionsPage.nima.features.f1Desc') || "Connect to everything. From CRMs like Salesforce to productivity tools like Slack, our library covers every major category your team uses daily."
    },
    {
      icon: FiSliders,
      title: t('solutionsPage.nima.features.f2Title') || "Smarter Compatibility",
      description: t('solutionsPage.nima.features.f2Desc') || "Experience smoother integrations with updated triggers and actions. Nima ensures your data flows exactly where it needs to, without the friction."
    },
    {
      icon: FiZap,
      title: t('solutionsPage.nima.features.f3Title') || "Instant Speed to Value",
      description: t('solutionsPage.nima.features.f3Desc') || "Don't wait weeks for custom builds. Use our ready-to-use connections to launch powerful automations in minutes, not days."
    },
    {
      icon: FiTrendingUp,
      title: t('solutionsPage.nima.features.f4Title') || "Focus on Real Growth",
      description: t('solutionsPage.nima.features.f4Desc') || "By automating the repetitive, your team is free to focus on attendee experience and strategy. Scale your event, not your workload."
    }
  ];

  const appCategories = [
    { name: 'CRM & Sales', apps: ['Salesforce', 'HubSpot', 'Pipedrive', 'Zoho CRM'] },
    { name: 'Team Chat & Alerts', apps: ['Slack', 'Microsoft Teams', 'Discord', 'Telegram'] },
    { name: 'Email & Marketing', apps: ['Mailchimp', 'ActiveCampaign', 'Klaviyo', 'Marketo'] },
    { name: 'Data & Storage', apps: ['Google Sheets', 'Airtable', 'PostgreSQL', 'Snowflake'] }
  ];

  return (
    <div className="space-y-12 py-2">
      {/* Hero Header */}
      <div className="text-center max-w-4xl mx-auto space-y-3">
        <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/60 backdrop-blur-md border border-white/60 text-cyan-700 text-xs font-semibold uppercase tracking-wider shadow-sm inner-glow">
          <SafeIcon icon={FiZap} className="w-3.5 h-3.5 text-cyan-600 animate-pulse" />
          <span>{t('solutionsPage.nima.badge') || "Automate 1,000+ App Workflows"}</span>
        </div>
        <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">
          {t('solutionsPage.nima.title') || "Connect Your Event Data to 1,000+ Apps"}
        </h2>
        <p className="text-base sm:text-lg text-slate-600 max-w-2xl mx-auto leading-relaxed">
          {t('solutionsPage.nima.subtitle') || "No more manual data entry and expensive third-party connectors. Nima bridges your event ecosystem with over 1,000 apps, turning complex workflows into seamless, automated growth."}
        </p>
      </div>

      {/* Interactive Workflow Automation Builder */}
      <div className="glass-panel rounded-[28px] p-6 md:p-8 border border-white/80 shadow-xl shadow-slate-200/40 inner-glow relative overflow-hidden space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 pb-5 border-b border-slate-200/70">
          <div>
            <div className="flex items-center gap-2.5">
              <span className="w-2.5 h-2.5 rounded-full bg-cyan-500 animate-pulse" />
              <h3 className="text-lg font-bold text-slate-900 tracking-tight">{t('solutionsPage.nima.engineTitle') || "Nima Event Automation Engine"}</h3>
            </div>
            <p className="text-xs text-slate-500 mt-0.5 font-medium">{t('solutionsPage.nima.engineSubtitle') || "Pre-built zero-code connectors for instantaneous data synchronization."}</p>
          </div>
          <span className="px-3.5 py-1 rounded-full bg-cyan-50 text-cyan-700 border border-cyan-200 text-xs font-bold font-mono">
            {t('solutionsPage.nima.integrationsCount') || "1,000+ Integrations"}
          </span>
        </div>

        {/* Live Visual Workflow Pipeline */}
        <div className="p-6 rounded-2xl bg-slate-900 text-white border border-slate-800 shadow-lg space-y-6">
          <div className="flex items-center justify-between text-xs border-b border-slate-800 pb-3">
            <span className="text-slate-400 font-mono">{t('solutionsPage.nima.pipelineLabel') || "PIPELINE: Attendee Onboarding & CRM Sync"}</span>
            <span className="text-emerald-400 font-mono flex items-center gap-1.5 font-bold">
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-ping" />
              {t('solutionsPage.nima.realtimeBadge') || "Real-Time Execution"}
            </span>
          </div>

          {/* Workflow Steps */}
          <div className="grid sm:grid-cols-4 gap-4 items-center">
            {/* Step 1: Trigger */}
            <div className="p-4 rounded-xl bg-white/10 border border-cyan-500/40 space-y-1.5 relative">
              <span className="text-[10px] font-bold uppercase tracking-wider text-cyan-400 font-mono">{t('solutionsPage.nima.s1Trigger') || "01. TRIGGER"}</span>
              <div className="text-xs font-bold text-white">{t('solutionsPage.nima.s1Title') || "New Ticket Sold"}</div>
              <p className="text-[11px] text-slate-300">{t('solutionsPage.nima.s1Desc') || "Pilot / Unity captures VIP ticket registration."}</p>
            </div>

            {/* Step 2: CRM Action */}
            <div className="p-4 rounded-xl bg-white/10 border border-white/10 space-y-1.5">
              <span className="text-[10px] font-bold uppercase tracking-wider text-blue-400 font-mono">{t('solutionsPage.nima.s2Sync') || "02. CRM SYNC"}</span>
              <div className="text-xs font-bold text-white">{t('solutionsPage.nima.s2Title') || "HubSpot / Salesforce"}</div>
              <p className="text-[11px] text-slate-300">{t('solutionsPage.nima.s2Desc') || "Creates contact record with custom badge tags."}</p>
            </div>

            {/* Step 3: Team Alert */}
            <div className="p-4 rounded-xl bg-white/10 border border-white/10 space-y-1.5">
              <span className="text-[10px] font-bold uppercase tracking-wider text-purple-400 font-mono">{t('solutionsPage.nima.s3Alert') || "03. TEAM ALERT"}</span>
              <div className="text-xs font-bold text-white">{t('solutionsPage.nima.s3Title') || "Slack Workspace"}</div>
              <p className="text-[11px] text-slate-300">{t('solutionsPage.nima.s3Desc') || "Sends instant VIP arrival ping to #event-ops."}</p>
            </div>

            {/* Step 4: Marketing Journey */}
            <div className="p-4 rounded-xl bg-white/10 border border-emerald-500/40 space-y-1.5">
              <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-400 font-mono">{t('solutionsPage.nima.s4Nurture') || "04. NURTURE"}</span>
              <div className="text-xs font-bold text-white">{t('solutionsPage.nima.s4Title') || "Email & WhatsApp"}</div>
              <p className="text-[11px] text-slate-300">{t('solutionsPage.nima.s4Desc') || "Dispatches QR access pass and calendar invites."}</p>
            </div>
          </div>
        </div>

        {/* Integration Categories Matrix */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 pt-2">
          {appCategories.map((cat, i) => (
            <div key={i} className="p-4 rounded-2xl bg-white/70 border border-slate-200/80 space-y-2">
              <h4 className="text-xs font-bold text-slate-900 uppercase tracking-wider text-cyan-800">{cat.name}</h4>
              <div className="flex flex-wrap gap-1.5">
                {cat.apps.map((app) => (
                  <span key={app} className="text-[11px] px-2 py-0.5 rounded-lg bg-slate-100 font-medium text-slate-700">
                    {app}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* The 4 Core Features Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
        {features.map((feat, idx) => (
          <motion.div
            key={idx}
            whileHover={{ y: -3 }}
            className="glass-card rounded-2xl p-6 border border-white/80 shadow-md shadow-slate-100/60 hover:shadow-xl hover:border-cyan-200/80 transition-all flex flex-col justify-between"
          >
            <div className="space-y-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500/10 to-blue-600/20 border border-cyan-200/60 flex items-center justify-center text-cyan-600 shadow-sm">
                <SafeIcon icon={feat.icon} className="w-6 h-6" />
              </div>
              <h3 className="text-base font-bold text-slate-900 tracking-tight">{feat.title}</h3>
              <p className="text-sm text-slate-600 leading-relaxed font-normal">{feat.description}</p>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

export default NimaTab;
